import { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";

import howItWorksImage from "@/assets/images/how-it-works.jpg";

// import howItWorksImageLg from "@/assets/images/how-it-works-lg.webp";
// import howItWorksImage from "@/assets/images/how-it-works-sm.webp";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../ui/carousel";

const steps = [
  {
    stepNumber: "1.",
    title: msg`Get in Touch`,
    description: msg`You provide unit photos, specify the number of bedrooms, and share the floor plan for screening our apartment. Simple and Fast.`,
  },
  {
    stepNumber: "2.",
    title: msg`Initial Inspection`,
    description: msg`Our expert inspectors conduct thorough property assessments to identify any property enhancements and maintenance requirements.`,
  },
  {
    stepNumber: "3.",
    title: msg`Contract Signed`,
    description: msg`We provide our thorough findings and unit plans, and agree on terms with the homeowner. One step away from being partners!`,
  },
  {
    stepNumber: "4.",
    title: msg`The Magic Begins`,
    description: msg`We professionally handle any maintenance works, décor or marketing, and list your property across our platform and multiple top channels.`,
  },
  {
    stepNumber: "5.",
    title: msg`Relax & Count Your Revenue`,
    description: msg`Sit back and watch your revenue grow while we handle everything from guest management to property maintenance.`,
  },
];
interface StepProps {
  title: MessageDescriptor;
  description: MessageDescriptor;
}

function Step({ title, description }: Readonly<StepProps>) {
  const { i18n } = useLingui();
  return (
    <div className="sm:h-[140px]">
      <h3 className="mb-2 line-clamp-1 flex-1 text-xl font-semibold">{i18n.t(title)}</h3>
      <p className="leading-relaxed font-medium sm:line-clamp-4">{i18n.t(description)}</p>
    </div>
  );
}

const placeholder = <div className="h-[140px]" />;

export default function HowItWorks() {
  return (
    <section className="container flex flex-col gap-10 rounded-4xl bg-(--color-bg-color-bg-dark-beige) px-4 py-10 md:px-8 lg:flex-row-reverse">
      <div className="relative flex-1 overflow-hidden">
        <h3 className="title-desktop-titlerg mb-6 max-w-[580px] md:mb-12">
          <Trans>
            How <span className="font-semibold">it works</span>
          </Trans>
        </h3>
        <div className="flex flex-col gap-6 md:hidden">
          {steps.map((step) => (
            <div key={step.stepNumber} className="flex gap-5">
              <span className="text-5xl font-semibold">{step.stepNumber}</span>
              <Step {...step} />
            </div>
          ))}
        </div>
        <Carousel
          className="static hidden md:block"
          opts={{
            align: "start",
            dragFree: true,
          }}
          plugins={[WheelGesturesPlugin()]}
        >
          <CarouselContent className="-ml-8">
            {steps.map((step, index) => (
              <CarouselItem
                key={step.stepNumber}
                className="pl-8 md:basis-[60%] xl:basis-[41%] 2xl:basis-[39.5%]"
              >
                <div className="h-full p-1">
                  {/* The structure of each card item */}
                  <div className="flex h-full flex-col justify-between gap-5 text-start">
                    {/* Text content for odd-indexed items goes on top */}
                    {index % 2 === 0 ? <Step {...step} /> : placeholder}

                    {/* The Number and Line */}
                    <div className="flex items-end">
                      <span className="text-[5rem] leading-none font-semibold">
                        {step.stepNumber}
                      </span>
                      <div className="flex-1 text-center">
                        <div className="ml-4 inline-block h-2 w-full max-w-36 rounded-full bg-(--color-stroke-stroke-separators)"></div>
                      </div>
                    </div>

                    {/* Text content for even-indexed items goes below */}
                    {index % 2 !== 0 ? <Step {...step} /> : placeholder}
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          {/* Custom Navigation Buttons */}
          <div className="absolute end-0 top-3 flex items-center gap-4">
            <CarouselPrevious className="static translate-y-0" />
            <CarouselNext className="static translate-y-0" />
          </div>
        </Carousel>
      </div>
      <div className="basis-1/3 self-center">
        <picture>
          <source srcSet={howItWorksImage} media="(min-width: 1024px)" width={920} height={1230} />
          <img
            src={howItWorksImage}
            alt="How it works"
            className="size-full rounded-4xl object-contain rtl:rotate-y-180"
            width={722}
            height={962}
            loading="lazy"
          />
        </picture>
      </div>
    </section>
  );
}
